import type { BufferAttribute, Material, Mesh, Object3D } from '@seddi/three';
import type { ResourcesContext } from '../resources/resourcesContext';
import { dispose as disposeLogoInternal } from './pieceLogoRenderable';
import { filterMeshes } from '../objects/filterObjects';
import { ModelLogoRenderable } from './modelLogoRenderable';

export type ModelRenderable = {
  readonly logos: ModelLogoRenderable[];
  readonly meshDecals: Mesh[];
  readonly parent: Object3D;
  resourceRef?: string;
};

export const create = (id: string, node: Object3D): ModelRenderable => {
  return { logos: [], meshDecals: [], parent: node };
};

export const dispose = (renderable: ModelRenderable, resources: ResourcesContext) => {
  detachModel(renderable, resources);
};

export const attachModel = (renderable: ModelRenderable, id: string, resources: ResourcesContext) => {
  // Add group to scene
  const group = resources.getRefModel(id)!;
  renderable.parent.add(group);
  renderable.resourceRef = id;
  // Gather mesh decals
  const meshes = filterMeshes(group);
  for (const mesh of meshes) {
    if (mesh.userData?.isMeshDecal) {
      renderable.meshDecals.push(mesh);
    }
  }
  // Update groups and materials
  recreateMaterials(renderable);
  recreateGroups(renderable);
};

export const detachModel = (renderable: ModelRenderable, resources: ResourcesContext) => {
  if (renderable.resourceRef) {
    // Remove from scene
    const group = resources.findModel(renderable.resourceRef)!;
    group.parent.remove(group);
    // Dispose logos
    const logos = renderable.logos;
    for (const logo of logos) {
      disposeLogoInternal(logo, resources);
    }
    logos.length = 0;
    // Update groups and materials to unmodify resource
    recreateMaterials(renderable);
    recreateGroups(renderable);
    renderable.meshDecals.length = 0;
    // Remove ref
    resources.retRefModel(renderable.resourceRef);
    delete renderable.resourceRef;
  }
};

/**
 * Recreate geometry groups.
 * @param renderable - Renderable.
 */
export const recreateGroups = (renderable: ModelRenderable) => {
  for (const mesh of renderable.meshDecals) {
    const geometry = mesh.geometry;
    const material = mesh.material as Material[];
    // Whole set of indices
    const start = 0;
    const index = geometry.index as BufferAttribute;
    const count = index.count;
    geometry.clearGroups();
    // Render with all available materials
    for (let i = 0; i < material.length; ++i) {
      geometry.addGroup(start, count, i);
    }
  }
};

/**
 * Recreate materials array.
 * @param renderable - Renderable.
 */
export const recreateMaterials = (renderable: ModelRenderable) => {
  // Push logos by mesh
  for (const mesh of renderable.meshDecals) {
    const material = mesh.material as Material[];
    material.length = 0;
    // Use original name instead of glTF loader modified name
    const name = mesh.userData.name ?? mesh.name;
    const logos = renderable.logos.filter((l) => l.meshId === name);
    for (const logo of logos) {
      if (logo.resourceRef) {
        material.push(logo.material);
      }
    }
  }
};

/**
 * Dispose logo.
 * @param renderable - Renderable.
 * @param id - Model logo ID.
 * @param resources - Resources context.
 */
export const disposeLogo = (renderable: ModelRenderable, id: string, resources: ResourcesContext) => {
  // Remove logo
  const logos = renderable.logos;
  const index = logos.findIndex((logo) => logo.id === id);
  disposeLogoInternal(logos[index], resources);
  logos.splice(index, 1);
  // Update groups and materials
  recreateMaterials(renderable);
  recreateGroups(renderable);
};
